import React, { ReactNode, useCallback } from 'react';
import FilterIcon from 'components/icon/filter-icon';
import CompassArrowIcon from 'components/icon/compass-arrow-icon';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { searchFilterIds } from 'constants/test-constants';
import ControlButton from './ControlButton';
import PlusIcon from 'components/icon/plus-icon';
import MinusIcon from 'components/icon/minus-icon';
import {
  TrackMapControlsFilterClick,
  TrackMapControlsMyLocationClick,
  TrackMapControlsZoomInClick,
  TrackMapControlsZoomOutClick,
} from 'components/search-es/tracking';

export type MapControlProps = {
  isVisible: boolean;
  onZoomInClick(): void;
  onZoomOutClick(): void;
  onGetUserLocationClick(): void;
  onFilterClick(): void;
  isAiSearch?: boolean;
  className?: string;
  hideFilterButton?: boolean;
  /** Renders the compact, grouped control stack (shared by the hybrid and search pages). */
  compact?: boolean;
  /** Compact-mode "my location" button. Omitted on the hybrid page; provided on the search page. */
  locationControl?: ReactNode;
  satelliteControl?: ReactNode;
  amenitiesControl?: ReactNode;
};

const MapControls = ({
  isVisible,
  onZoomInClick,
  onZoomOutClick,
  onGetUserLocationClick,
  onFilterClick,
  isAiSearch,
  hideFilterButton,
  className,
  compact = false,
  locationControl,
  satelliteControl,
  amenitiesControl,
}: MapControlProps) => {
  const handleZoomInClick = useCallback(() => {
    TrackMapControlsZoomInClick();
    onZoomInClick();
  }, [onZoomInClick]);

  const handleZoomOutClick = useCallback(() => {
    TrackMapControlsZoomOutClick();
    onZoomOutClick();
  }, [onZoomOutClick]);

  const handleFilterClick = useCallback(() => {
    TrackMapControlsFilterClick();
    onFilterClick();
  }, [onFilterClick]);

  const handleGetUserLocationClick = useCallback(() => {
    TrackMapControlsMyLocationClick();
    onGetUserLocationClick();
  }, [onGetUserLocationClick]);

  return (
    <div
      className={buildClassName(
        styles.component,
        isVisible && styles['not-visible'],
        isAiSearch && styles['is-ai-search'],
        compact && styles['controls-compact'],
        className
      )}
    >
      {isAiSearch && <p>MapControls</p>}
      <ControlButton desktopOnly={true} handler={handleZoomInClick} testId={searchFilterIds.mapZoomIn}>
        <PlusIcon />
      </ControlButton>
      <ControlButton desktopOnly={true} handler={handleZoomOutClick}>
        <MinusIcon />
      </ControlButton>
      {compact ? (
        <>
          {locationControl}
          {satelliteControl}
          {amenitiesControl}
        </>
      ) : (
        <>
          {!isAiSearch && !hideFilterButton && (
            <ControlButton mobileOnly={true} handler={handleFilterClick}>
              <FilterIcon className={styles.filter} />
            </ControlButton>
          )}
          <ControlButton handler={handleGetUserLocationClick}>
            <CompassArrowIcon className={styles['compass-arrow']} />
          </ControlButton>
        </>
      )}
    </div>
  );
};

export default MapControls;
